import { Component, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { CompanyVoteResultSearchBoxComponent } from '../company-vote-result-search-box/company-vote-result-search-box.component';
import { CompanyVoteResultSearchFilter } from '../../../models/companyVoteResultSearchFilter';
import { CompanyVoteResultService } from '../../../services/company-vote-result.service';
import { CompanyVoteResultSearchDto } from '../../../models/api/companyVoteResultSearchDto';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { SetSearchFilter, SetPagination, SetSort } from '../../../store/actions/company-vote-result-list.actions';
import { selectCompanyVoteResultListFullState } from '../../../store/selectors/company-vote-result-list.selectors';
import { take, filter } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-company-vote-result-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    GenericListComponent,
    NgxSpinnerModule,
    CompanyVoteResultSearchBoxComponent
  ],
  templateUrl: './company-vote-result-list.component.html',
  styleUrl: './company-vote-result-list.component.scss'
})
export class CompanyVoteResultListComponent implements OnDestroy {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<CompanyVoteResultSearchDto>;

  voteResults: CompanyVoteResultSearchDto[] = [];
  totalItems = 0;
  isSearchVisible = true;

  currentPage = 1;
  pageSize = 10;
  sortBy = 'companyName';
  ascending = true;
  currentSearchFilter: CompanyVoteResultSearchFilter = {};

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'companyName', displayName: 'Company' },
    { propertyName: 'meetingDate', displayName: 'Meeting Date', dataType: 'date' },
    { propertyName: 'issue', displayName: 'Issue' },
    { propertyName: 'proposalName', displayName: 'Proposal Name' },
    { propertyName: 'categoryName', displayName: 'Category' },
    { propertyName: 'subcategoryName', displayName: 'Subcategory' },
    { propertyName: 'isShareholderProposal', displayName: 'Shareholder Proposal', dataType: 'boolean' },
  ];

  constructor(
    private router: Router,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private companyVoteResultService: CompanyVoteResultService,
    private store: Store<IAppState>,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.store.select(selectCompanyVoteResultListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      this.loadVoteResults();
    });
  }

  private loadVoteResults(): void {
    this.spinnerService.show();

    this.companyVoteResultService.search({
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        this.voteResults = response.data;
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading voting results:', error);
        this.toast.error('Error loading voting results');
        this.spinnerService.hide();
      }
    });
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';

    this.store.dispatch(new SetSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    this.loadVoteResults();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    this.store.dispatch(new SetPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    this.loadVoteResults();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onSearchFilter(searchFilter: CompanyVoteResultSearchFilter): void {
    this.currentSearchFilter = searchFilter;
    this.currentPage = 1;

    this.store.dispatch(new SetSearchFilter(searchFilter));

    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    this.loadVoteResults();
  }

  onExportToCsv(): void {
    this.spinnerService.show();

    this.companyVoteResultService.exportToCsv({
      ...this.currentSearchFilter,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `voting_results_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.toast.success('Voting results exported successfully');
      },
      error: (error) => {
        console.error('Error exporting voting results:', error);
        this.toast.error('Error exporting voting results');
        this.spinnerService.hide();
      }
    });
  }

  onLinkToCompany(voteResult: CompanyVoteResultSearchDto): void {
    if (!voteResult.companyId) {
      this.toast.warning('Company ID not available for this voting result');
      return;
    }

    const queryParams: any = { tab: 'votingResults' };
    if (voteResult.year) {
      queryParams.year = voteResult.year.toString();
    }

    this.router.navigate(['/companies/edit', voteResult.companyId], { queryParams });
  }

  onDeleteVoteResult(voteResult: CompanyVoteResultSearchDto): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(voteResult.companyName || 'this vote result')
      .subscribe(result => {
        if (result) {
          const originalVoteResults = this.voteResults;
          const originalTotalItems = this.totalItems;

          this.voteResults = this.voteResults.filter(v => v.id !== voteResult.id);
          this.totalItems = this.totalItems - 1;

          this.spinnerService.show();
          this.companyVoteResultService.delete(voteResult.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Voting result deleted successfully');
              this.loadVoteResults();
            },
            error: (error) => {
              console.error('Error deleting voting result:', error);
              this.spinnerService.hide();
              this.voteResults = originalVoteResults;
              this.totalItems = originalTotalItems;
              this.toast.error(`<strong>Failed to delete Voting Result:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  ngOnDestroy(): void {}
}
